import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Globe,
  Phone,
  Mail,
  Calendar,
  Users,
  Building2,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Target,
  Eye,
  Heart,
  Sparkles,
  ArrowRight,
  X,
  Info,
  Ticket,
  Clock,
  Star,
  Zap,
  TrendingUp,
  Layers,
} from "lucide-react";
import { IOrganizationProps } from "../../../../types/Organization";
import { IEventProps } from "../../../../types/Event";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { DashboardEndpoints } from "../../../../lib/endpoints-dashboard";
import { Loader } from "../../../../components/ui/loader";
import "./styles.scss";

interface HomepageThemeFourProps {
  organizationId: number;
  primaryColor?: string;
  secondaryColor?: string;
}

const HomepageThemeFour: React.FC<HomepageThemeFourProps> = ({
  organizationId,
  primaryColor = "#6366f1",
  secondaryColor = "#ffffff",
}) => {
  const [selectedEvent, setSelectedEvent] = useState<IEventProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set CSS variables for dynamic theming
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--theme-primary-color",
      primaryColor
    );
    document.documentElement.style.setProperty(
      "--theme-secondary-color",
      secondaryColor
    );
  }, [primaryColor, secondaryColor]);

  // Fetch organization data
  const organizationEndpoint = useMemo(() => {
    return DashboardEndpoints.GetSingleOrganization(organizationId);
  }, [organizationId]);

  const { data: orgResponse, isLoading: isLoadingOrg } =
    usePerformRequest<IOrganizationProps>({
      method: organizationEndpoint.method as any,
      route: organizationEndpoint.route,
      doNotUseAuthorization: true,
    });

  const organization = useMemo(() => {
    if (!orgResponse) return null;
    const data = (orgResponse as any)?.data || orgResponse;
    return data as IOrganizationProps;
  }, [orgResponse]);

  // Fetch events data
  const eventsEndpoint = useMemo(() => {
    if (!organizationId) return null;
    return DashboardEndpoints.GetOrganizationEvents(organizationId);
  }, [organizationId]);

  const { data: eventsResponse, isLoading: isLoadingEvents } =
    usePerformRequest<IEventProps[]>({
      method: eventsEndpoint?.method as any,
      route: eventsEndpoint?.route || "",
      doNotUseAuthorization: true,
    });

  const events = useMemo(() => {
    if (!eventsResponse) return [];
    let data = eventsResponse;
    if ((eventsResponse as any)?.data) {
      data = (eventsResponse as any).data;
    }
    if (Array.isArray(data)) {
      return data as IEventProps[];
    }
    if (data && typeof data === "object" && (data as any).events) {
      const eventsArray = Array.isArray((data as any).events)
        ? ((data as any).events as IEventProps[])
        : [];
      return eventsArray;
    }
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return [data] as IEventProps[];
    }
    return [];
  }, [eventsResponse]);

  // Social links
  const socialLinks = useMemo(() => {
    if (!organization) return [];
    return [
      { icon: Facebook, url: organization.facebook_link, label: "Facebook" },
      { icon: Instagram, url: organization.instagram_link, label: "Instagram" },
      { icon: Linkedin, url: organization.linkedin_link, label: "LinkedIn" },
      { icon: Twitter, url: organization.x_link, label: "Twitter" },
      { icon: Youtube, url: organization.youtube_link, label: "YouTube" },
    ].filter((link) => link.url);
  }, [organization]);

  // Location string
  const locationString = useMemo(() => {
    if (!organization?.location) return "";
    const { address, city, state, country } = organization.location;
    const parts = [address, city, state, country].filter(Boolean);
    return parts.join(", ");
  }, [organization]);

  // Format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "TBD";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "TBD";
    }
  };

  // Format time
  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  if (isLoadingOrg || isLoadingEvents) {
    return (
      <div className="theme-four-loading">
        <Loader size="lg" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="theme-four-error">
        <p>Organization not found</p>
      </div>
    );
  }

  const displayedEvents = events.slice(0, 6);

  return (
    <div
      className="theme-four"
      style={
        {
          "--theme-primary-color": primaryColor,
          "--theme-secondary-color": secondaryColor,
        } as React.CSSProperties
      }
    >
      {/* Hero Section */}
      <motion.section
        className="theme-four-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-logo"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            {organization.logo && (
              <img src={organization.logo} alt={organization.name} />
            )}
          </motion.div>
          <motion.h1
            className="hero-title"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {organization.name}
          </motion.h1>
          {organization.subtitle && (
            <motion.p
              className="hero-subtitle"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {organization.subtitle}
            </motion.p>
          )}
          <motion.div
            className="hero-stats"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="stat-card">
              <Zap className="stat-icon" />
              <div className="stat-content">
                <div className="stat-value">
                  {organization.number_of_locations || 0}
                </div>
                <div className="stat-label">Locations</div>
              </div>
            </div>
            <div className="stat-card">
              <TrendingUp className="stat-icon" />
              <div className="stat-content">
                <div className="stat-value">
                  {organization.years_of_experience || 0}+
                </div>
                <div className="stat-label">Years</div>
              </div>
            </div>
            <div className="stat-card">
              <Calendar className="stat-icon" />
              <div className="stat-content">
                <div className="stat-value">{events.length}</div>
                <div className="stat-label">Events</div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* About Section */}
      {organization.about && (
        <motion.section
          className="theme-four-about"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="about-container">
            <motion.div
              className="about-header"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title">About Us</h2>
              {organization.description && (
                <p className="about-description">{organization.description}</p>
              )}
            </motion.div>
            <div className="about-cards">
              {organization.about.mission && (
                <motion.div
                  className="about-card glass-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                >
                  <Target className="card-icon" />
                  <h3>Mission</h3>
                  <p>{organization.about.mission}</p>
                </motion.div>
              )}
              {organization.about.vision && (
                <motion.div
                  className="about-card glass-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                >
                  <Eye className="card-icon" />
                  <h3>Vision</h3>
                  <p>{organization.about.vision}</p>
                </motion.div>
              )}
              {organization.about.values && (
                <motion.div
                  className="about-card glass-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                >
                  <Layers className="card-icon" />
                  <h3>Values</h3>
                  <p>{organization.about.values}</p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.section>
      )}

      {/* Events Section */}
      {displayedEvents.length > 0 && (
        <motion.section
          className="theme-four-events"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="events-container">
            <motion.div
              className="events-header"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title">Upcoming Events</h2>
              <p className="section-subtitle">
                Join us for these exciting experiences
              </p>
            </motion.div>
            <div className="events-grid">
              {displayedEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  className="event-card glass-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  onClick={() => {
                    setSelectedEvent(event);
                    setIsModalOpen(true);
                  }}
                >
                  {event.cover_image && (
                    <div className="event-image">
                      <img src={event.cover_image} alt={event.name} />
                      <div className="event-overlay"></div>
                    </div>
                  )}
                  <div className="event-content">
                    <div className="event-date">
                      <Calendar size={16} />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <h3 className="event-title">{event.name}</h3>
                    {event.event_description && (
                      <p className="event-description">
                        {event.event_description.slice(0, 100)}...
                      </p>
                    )}
                    <div className="event-meta">
                      {event.type && (
                        <div className="event-type">
                          <Globe size={14} />
                          <span>{event.type}</span>
                        </div>
                      )}
                      {event.number_of_seats && (
                        <div className="event-seats">
                          <Users size={14} />
                          <span>{event.number_of_seats} seats</span>
                        </div>
                      )}
                    </div>
                    <button className="event-button">
                      View Details <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* Location Section */}
      {locationString && (
        <motion.section
          className="theme-four-location"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="location-container">
            <motion.div
              className="location-content glass-card"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <MapPin className="location-icon" />
              <h2 className="section-title">Our Location</h2>
              <p className="location-address">{locationString}</p>
              {organization.phone_number && (
                <div className="contact-info">
                  <Phone size={18} />
                  <span>{organization.phone_number}</span>
                </div>
              )}
              {organization.email && (
                <div className="contact-info">
                  <Mail size={18} />
                  <span>{organization.email}</span>
                </div>
              )}
              {organization.website && (
                <div className="contact-info">
                  <Globe size={18} />
                  <a
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {organization.website}
                  </a>
                </div>
              )}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Footer */}
      <motion.footer
        className="theme-four-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              {organization.logo && (
                <img src={organization.logo} alt={organization.name} />
              )}
              <h3>{organization.name}</h3>
              {organization.subtitle && <p>{organization.subtitle}</p>}
            </div>
            {socialLinks.length > 0 && (
              <div className="footer-social">
                <h4>Connect With Us</h4>
                <div className="social-links">
                  {socialLinks.map((link, index) => {
                    const Icon = link.icon;
                    return (
                      <motion.a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, rotate: 5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Icon size={20} />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="footer-bottom">
            <p>
              © {new Date().getFullYear()} {organization.name}. All rights
              reserved.
            </p>
          </div>
        </div>
      </motion.footer>

      {/* Event Modal */}
      <AnimatePresence mode="wait">
        {isModalOpen && selectedEvent && (
          <motion.div
            className="event-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="event-modal glass-card"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, x: "-50%", y: "-50%" }}
              exit={{ scale: 0.8, opacity: 0, x: "-50%", y: "-50%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                top: "50%",
                left: "50%",
              }}
            >
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                <X size={24} />
              </button>
              {selectedEvent.cover_image && (
                <div className="modal-image">
                  <img
                    src={selectedEvent.cover_image}
                    alt={selectedEvent.name}
                  />
                </div>
              )}
              <div className="modal-content">
                <div className="modal-header">
                  <div className="modal-date">
                    <Calendar size={18} />
                    <span>{formatDate(selectedEvent.date)}</span>
                  </div>
                  {selectedEvent.is_paid_event && (
                    <div className="modal-badge paid">
                      <Ticket size={16} />
                      <span>Paid Event</span>
                    </div>
                  )}
                </div>
                <h2 className="modal-title">{selectedEvent.name}</h2>
                {selectedEvent.event_description && (
                  <p className="modal-description">
                    {selectedEvent.event_description}
                  </p>
                )}
                <div className="modal-details">
                  {selectedEvent.type && (
                    <div className="detail-item">
                      <Globe size={18} />
                      <span>{selectedEvent.type}</span>
                    </div>
                  )}
                  {selectedEvent.number_of_seats && (
                    <div className="detail-item">
                      <Users size={18} />
                      <span>
                        {selectedEvent.number_of_seats} seats available
                      </span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="detail-item">
                      <MapPin size={18} />
                      <span>
                        {selectedEvent.location.city ||
                          selectedEvent.location.address ||
                          "Location TBA"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomepageThemeFour;
